import QuestionMCQ from "@/components/quiz/components/QuestionMCQ";
import useQuizHandler from "@/components/quiz/hooks/useQuizHandler";
import QuizTimer from "@/components/quiz/QuizTimer";
import {
  AssessmentMode,
  LessonDataI,
  QuestionData,
} from "@/components/quiz/typesAndEnums";
import Spinner from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { LessonScreenI } from "@/hooks/types";
import { useAppSelector } from "@/store/hooks";
import { ArrowLeft, ChevronLeft, ChevronRight, Clock } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import QuestionGrid from "./QuestionGrid";

const LessonQuiz = ({
  lessonData,
  setScreenContent,
}: {
  lessonData: LessonDataI;
  setScreenContent: React.Dispatch<React.SetStateAction<LessonScreenI>>;
}) => {
  const {
    score,
    maxScore,
    percentage,
    isEnded,
    isStarted,
    isExpired,
    remainingTime,
    startedTimestamp,
    questions,
    isSubmitPending,
    passed,
    submitError,
    handleSubmitQuiz,
  } = useQuizHandler({ lessonData });

  if (submitError) toast.error("حدث خطأ اثناء تسليم الإمتحان ، تواصل مع الدعم");


  const { currentQuizProgress } = useAppSelector((state) => state.store);

  const [currentIndex, setCurrentIndex] = useState<number>(0);

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const calculateGridBoxClassName = (question: QuestionData, index: number) => {
    const answer = currentQuizProgress?.[question?.question_id];
    const correctAnswer = question?.correct_answer;

    if (index === currentIndex)
      return "bg-blue-600 text-white hover:bg-blue-600";

    if (isEnded) {
      if (!answer) return "bg-slate-100 text-slate-600 hover:bg-slate-200";
      if (answer !== correctAnswer)
        return "bg-red-500 border border-red-500 text-white";
      if (answer === correctAnswer)
        return "bg-green-600 border border-green-600 text-white";
    } else {
      return answer
        ? "bg-slate-300 text-slate-600 hover:bg-slate-400"
        : "bg-slate-100 text-slate-600 hover:bg-slate-200";
    }

    return "";
  };

  if (!lessonData.quiz_id)
    return (
      <div className="p-5 d-flex">
        <h2 className="m-auto">لا يوجد كويز</h2>
      </div>
    );

  if (isExpired)
    return (
      <div
        className="bg-red-100 border border-red-400 text-red-600 m-5 rounded-ea"
        role="alert"
      >
        إنتهى الوقت المسموح لدخول الكويز , تواصل مع المدرس
      </div>
    );

  if (!lessonData || !questions || (!isStarted && !isEnded))
    return (
      <div className="h-[calc(100vh-200px)] w-full flex items-center justify-center">
        <Spinner />
      </div>
    );

  const progressPercent = isEnded
    ? percentage
    : (Object.keys(currentQuizProgress ?? {}).length / questions.length) * 100;

  const isSubmitDisabled =
    isSubmitPending ||
    isEnded ||
    (lessonData.must_answer_all &&
      Object.keys(currentQuizProgress ?? {}).length < questions.length);

  return (
    <div className="min-h-screen">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 pt-4">
        <Button
          className="bg-primary rounded-xl w-fit mb-4 cursor-pointer"
          onClick={() =>
            setScreenContent((prev: LessonScreenI) => ({
              ...prev,
              elementId: "",
            }))
          }
        >
          <ArrowLeft className="text-white size-5 mx-3" />
        </Button>
        {/* Header */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="text-2xl">
                {lessonData.quiz_title}
              </CardTitle>
              <div className="flex items-center space-x-4">
                {!isEnded && (
                  <div className="flex items-center text-red-600">
                    <QuizTimer
                      handleSubmitQuiz={handleSubmitQuiz}
                      seconds={remainingTime}
                      isEnded={isEnded}
                      startedTimestamp={startedTimestamp}
                      quizDuration={Number(lessonData?.quiz_duration)}
                    />
                    <Clock className="w-5 h-5 mr-2" />
                  </div>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm text-gray-600">
                {isEnded ? (
                  <span
                    className={`font-semibold ${
                      passed ? "text-green-600" : "text-red-600"
                    }`}
                  >
                    الدرجة: {score} / {maxScore}
                  </span>
                ) : (
                  <span>
                    تم حل {Object.keys(currentQuizProgress ?? {})?.length} من{" "}
                    {questions.length}
                  </span>
                )}
                {isEnded ? (
                  <span>تم حل %{Math.round(progressPercent)}</span>
                ) : (
                  <span>{Math.round(progressPercent)}% مكتمل</span>
                )}
              </div>
              <Progress value={progressPercent} className="w-full" />
            </div>
          </CardHeader>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Question Content */}
          <div className="lg:col-span-3">
            <QuestionMCQ
              key={questions[currentIndex].question_id}
              assessmentId={lessonData?.quiz_id}
              question={questions[currentIndex]}
              isEnded={isEnded}
              mustAnswerAll={lessonData.must_answer_all}
              hideCorrectAnswers={lessonData.hide_correct_answers}
              assessmentMode={AssessmentMode.QUIZ}
            />

            {/* Navigation Buttons */}
            <div className="flex justify-between">
              <Button
                onClick={handlePrevious}
                disabled={currentIndex === 0}
                className="flex items-center"
              >
                <ChevronRight className="w-4 h-4 ml-2" />
                السابق
              </Button>

              <Button
                onClick={handleNext}
                className="flex items-center"
                disabled={currentIndex === questions.length - 1}
              >
                التالي
                <ChevronLeft className="w-4 h-4 mr-2" />
              </Button>
            </div>
          </div>

          {/* Question Grid Navigation */}
          <div className="lg:col-span-1">
            <QuestionGrid
              questions={questions}
              currentIndex={currentIndex}
              currentProgress={currentQuizProgress}
              isEnded={isEnded}
              setCurrentIndex={setCurrentIndex}
              calculateGridBoxClassName={calculateGridBoxClassName}
              handleSubmitQuiz={handleSubmitQuiz}
              isSubmitDisabled={isSubmitDisabled}
              isSubmitPending={isSubmitPending}
              SubmitButtonContent={
                isSubmitPending ? (
                  <Spinner variant="button" />
                ) : (
                  "تسليم الإمتحان"
                )
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LessonQuiz;
